<template>
  <div class="cv" @click="onRootClick" @contextmenu.prevent="onRootContext">
    <header class="cv-top">
      <div class="cv-top-left">
        <button class="cv-icon-btn" :title="$t('designStudio.all_designs')" @click.stop="$router.push('/ai/studio')">
          <i class="fas fa-arrow-left"></i>
        </button>
        <select class="cv-size" :value="currentSizeId" @change="onSizeSelect">
          <option v-for="s in sizes" :key="s.id" :value="s.id">{{ s.name }} · {{ s.w }}×{{ s.h }}</option>
          <option value="custom">Custom {{ page.width }}×{{ page.height }}</option>
        </select>
        <input v-model="name" class="cv-title" :placeholder="$t('designStudio.design_name')" @change="dirty = true" />
      </div>
      <div class="cv-top-right">
        <button class="cv-icon-btn" :disabled="!canUndo" :title="$t('designStudio.undo')" @click="undo"><i class="fas fa-undo"></i></button>
        <button class="cv-icon-btn" :disabled="!canRedo" :title="$t('designStudio.redo')" @click="redo"><i class="fas fa-redo"></i></button>
        <button
          class="cv-icon-btn cv-report-btn"
          :title="$t('designStudio.report_canva_import')"
          @click="openCanvaReport"
        >
          <i class="fas fa-flag"></i>
        </button>
        <span class="cv-autosave">{{ saving ? $t('designStudio.saving') : (dirty ? $t('designStudio.unsaved') : $t('designStudio.saved')) }}</span>
      </div>
    </header>

    <div class="cv-body">
      <nav class="cv-rail">
        <button
          v-for="tab in rails"
          :key="tab.id"
          class="cv-rail-btn"
          :class="{ on: leftTab === tab.id }"
          @click="toggleTab(tab.id)"
        >
          <i :class="['fas', tab.icon]"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <aside v-if="leftTab" class="cv-panel">
        <div class="cv-panel-head">
          <h3>{{ panelTitle }}</h3>
          <button class="cv-icon-btn" @click="leftTab = null"><i class="fas fa-times"></i></button>
        </div>
        <div class="cv-search">
          <i class="fas fa-search"></i>
          <input v-model="query" :placeholder="searchPlaceholder" />
        </div>
        <div class="cv-panel-body">
          <template v-if="leftTab === 'design'">
            <p class="cv-sec">{{ $t('designStudio.templates') }}</p>
            <div class="cv-grid">
              <button v-for="tpl in filteredTemplates" :key="tpl.name" class="cv-card" @click="useStarter(tpl)">
                <div class="cv-thumb">
                  <DesignPreview :template="tpl" :max-size="100" flat />
                </div>
                <span>{{ tpl.name }}</span>
              </button>
            </div>
          </template>

          <template v-else-if="leftTab === 'elements'">
            <p class="cv-sec">{{ $t('designStudio.shapes') }}</p>
            <div class="cv-shapes">
              <button v-for="s in shapes" :key="s.label" class="cv-shape" :title="s.label" @click="addShape(s)">
                <span :style="shapePreview(s)"></span>
              </button>
            </div>
            <p class="cv-sec">{{ $t('designStudio.stickers') }}</p>
            <div class="cv-stickers">
              <button v-for="s in stickers" :key="s.icon" :title="s.label" @click="addSticker(s)">
                <i :class="['fas', `fa-${s.icon}`]"></i>
              </button>
            </div>
            <p class="cv-sec">{{ $t('designStudio.background_palettes') }}</p>
            <div class="cv-palette-grid">
              <button v-for="p in palettes" :key="p.name" class="cv-palette-card" :title="`Apply ${p.name}`" @click="applyPalette(p)">
                <div class="cv-palette-preview" :style="palettePreviewStyle(p)">
                  <span class="cv-palette-blob cv-palette-blob--a" :style="{ background: p.colors[1] }"></span>
                  <span class="cv-palette-blob cv-palette-blob--b" :style="{ background: p.colors[2] }"></span>
                  <span class="cv-palette-sample" :style="{ color: paletteTextColor(p) }">Aa</span>
                </div>
                <div class="cv-palette-meta">
                  <span>{{ p.name }}</span>
                  <div class="cv-palette-dots">
                    <span v-for="c in p.colors" :key="c" :style="{ background: c }"></span>
                  </div>
                </div>
              </button>
            </div>
          </template>

          <template v-else-if="leftTab === 'text'">
            <button class="cv-magic" :disabled="aiLoading" @click="onMagicWrite">
              <i class="fas fa-magic"></i> {{ aiLoading ? $t('designStudio.writing') : $t('designStudio.magic_write') }}
            </button>
            <input v-model="aiPrompt" class="cv-input" :placeholder="$t('designStudio.headline_placeholder')" />
            <button
              v-for="tp in textPresets"
              :key="tp.style"
              class="cv-text-add"
              :class="tp.style"
              @click="addText(tp.layer)"
            >{{ tp.preview }}</button>
            <p class="cv-sec">{{ $t('designStudio.font_combinations') }}</p>
            <button v-for="combo in textCombos" :key="combo.name" class="cv-combo" @click="addCombo(combo)">
              <strong>{{ combo.name }}</strong>
              <span>{{ $t('designStudio.heading_subtext') }}</span>
            </button>
            <p class="cv-sec">{{ $t('designStudio.fonts') }}</p>
            <button v-for="f in fonts" :key="f" class="cv-font" :style="{ fontFamily: f }" @click="addText({ fontFamily: f, text: f })">{{ f }}</button>
          </template>

          <template v-else-if="leftTab === 'uploads'">
            <input ref="upInput" type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple class="d-none" @change="onUploadFiles" />
            <button class="cv-upload-btn" @click="$refs.upInput.click()">
              <i class="fas fa-cloud-upload-alt"></i>
              {{ $t('designStudio.upload_media') }}
            </button>
            <p class="cv-hint">{{ $t('designStudio.upload_hint') }}</p>
            <div class="cv-grid">
              <button v-for="u in uploads" :key="u" class="cv-card" @click="addMedia(u)">
                <div class="cv-thumb">
                  <video v-if="isVideoSrc(u)" :src="u" muted playsinline preload="metadata" />
                  <img v-else class="cv-thumb-img" :src="u" alt="" />
                </div>
              </button>
            </div>
          </template>

        </div>
      </aside>

      <div class="cv-work">
        <div v-if="selectedLayer && !editingId" class="cv-float" @click.stop>
          <template v-if="selectedLayer.type === 'text'">
            <div class="cv-dd">
              <button
                class="cv-fnt"
                :class="{ on: menu === 'font' }"
                :style="{ fontFamily: selectedLayer.fontFamily }"
                type="button"
                title="Font"
                @click="toggleMenu('font')"
              >
                <span>{{ selectedLayer.fontFamily }}</span>
                <i class="fas fa-caret-down"></i>
              </button>
              <div v-if="menu === 'font'" class="cv-pop cv-fnt-pop" @click.stop>
                <input
                  ref="fontSearch"
                  v-model="fontQuery"
                  class="cv-fnt-search"
                  type="text"
                  placeholder="Search fonts"
                  @keydown.esc.stop.prevent="closeFontMenu"
                  @keydown.down.prevent="moveFontHi(1)"
                  @keydown.up.prevent="moveFontHi(-1)"
                  @keydown.enter.prevent="pickHighlightedFont"
                />
                <div ref="fontList" class="cv-fnt-list">
                  <button
                    v-for="(f, i) in filteredFonts"
                    :key="f"
                    type="button"
                    class="cv-fnt-opt"
                    :class="{ on: f === selectedLayer.fontFamily, hi: i === fontHi }"
                    :style="{ fontFamily: f }"
                    @mouseenter="fontHi = i"
                    @click="pickFont(f)"
                  >{{ f }}</button>
                  <p v-if="!filteredFonts.length" class="cv-fnt-empty">No fonts found</p>
                </div>
              </div>
            </div>
            <input v-model.number="selectedLayer.fontSize" type="number" min="8" max="400" class="cv-sz" title="Font size" @change="pushHistory()" />
            <span class="cv-sep"></span>
            <label class="cv-col" :style="{ background: selectedLayer.color }" title="Color">
              <input type="color" v-model="selectedLayer.color" />
            </label>
            <button class="cv-tbtn" :class="{ on: selectedLayer.fontWeight === '700' || selectedLayer.fontWeight === '800' }" title="Bold" @click="toggleBold"><b>B</b></button>
            <button class="cv-tbtn" :class="{ on: selectedLayer.fontStyle === 'italic' }" title="Italic" @click="selectedLayer.fontStyle = selectedLayer.fontStyle === 'italic' ? 'normal' : 'italic'; pushHistory()"><i>I</i></button>
            <button class="cv-tbtn" :class="{ on: selectedLayer.underline }" title="Underline" @click="selectedLayer.underline = !selectedLayer.underline; pushHistory()"><u>U</u></button>
            <button class="cv-tbtn" title="Align" @click="cycleAlign"><i :class="alignIcon"></i></button>
            <button class="cv-tbtn" :class="{ on: selectedLayer.uppercase }" title="Uppercase" @click="selectedLayer.uppercase = !selectedLayer.uppercase; pushHistory()">TT</button>
            <span class="cv-sep"></span>
            <div class="cv-dd">
              <button class="cv-tbtn" :class="{ on: menu === 'effects' }" title="Effects" @click="toggleMenu('effects')"><i class="fas fa-adjust"></i></button>
              <div v-if="menu === 'effects'" class="cv-pop" @click.stop>
                <label><input type="checkbox" v-model="selectedLayer.shadow" /> Shadow</label>
                <label><input type="checkbox" v-model="selectedLayer.outline" /> Outline</label>
                <label>Outline color <input type="color" v-model="selectedLayer.outlineColor" /></label>
              </div>
            </div>
            <button class="cv-tbtn" :disabled="aiLoading" title="Magic Write" @click="onMagicWrite"><i class="fas fa-magic"></i></button>
          </template>
          <template v-else-if="selectedLayer.type === 'video'">
            <button class="cv-tbtn" :title="selectedLayer.paused ? 'Play' : 'Pause'" @click="toggleVideoPlay(selectedLayer)">
              <i :class="['fas', selectedLayer.paused ? 'fas fa-play' : 'fas fa-pause']"></i>
            </button>
            <button class="cv-tbtn" :class="{ on: !selectedLayer.muted }" :title="selectedLayer.muted ? 'Unmute' : 'Mute'" @click="toggleVideoMute(selectedLayer)">
              <i :class="['fas', selectedLayer.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up']"></i>
            </button>
            <button class="cv-tbtn" :class="{ on: selectedLayer.loop }" title="Loop" @click="selectedLayer.loop = !selectedLayer.loop; pushHistory()">
              <i class="fas fa-redo"></i>
            </button>
            <input
              class="cv-seek"
              type="range"
              min="0"
              :max="selectedLayer._dur || 0"
              step="0.05"
              :value="selectedLayer._time || 0"
              title="Seek"
              @mousedown.stop
              @input="seekVideo(selectedLayer, $event)"
            />
            <span class="cv-lab">Opacity</span>
            <input v-model.number="selectedLayer.opacity" type="range" min="0.05" max="1" step="0.05" class="cv-range" />
          </template>
          <template v-else-if="selectedLayer.type === 'icon'">
            <label class="cv-col" :style="{ background: selectedLayer.color }" title="Color">
              <input type="color" v-model="selectedLayer.color" />
            </label>
            <span class="cv-lab">Opacity</span>
            <input v-model.number="selectedLayer.opacity" type="range" min="0.05" max="1" step="0.05" class="cv-range" />
          </template>
          <template v-else-if="selectedLayer.type === 'shape'">
            <label class="cv-col" :style="{ background: selectedLayer.fill }" title="Fill">
              <input type="color" :value="hexFill" @input="selectedLayer.fill = $event.target.value" />
            </label>
            <label class="cv-col ring" :style="{ background: selectedLayer.stroke === 'transparent' ? '#fff' : selectedLayer.stroke }" title="Stroke">
              <input type="color" :value="hexStroke" @input="selectedLayer.stroke = $event.target.value; if (!selectedLayer.strokeWidth) selectedLayer.strokeWidth = 4" />
            </label>
            <span class="cv-lab">Opacity</span>
            <input v-model.number="selectedLayer.opacity" type="range" min="0.05" max="1" step="0.05" class="cv-range" />
          </template>
          <template v-else>
            <button class="cv-tbtn lbl" @click="selectedLayer.filter = 'none'">Original</button>
            <button class="cv-tbtn lbl" @click="selectedLayer.filter = 'grayscale'">B&amp;W</button>
            <button class="cv-tbtn lbl" @click="selectedLayer.filter = 'sepia'">Sepia</button>
            <button class="cv-tbtn lbl" @click="selectedLayer.filter = 'contrast'">Pop</button>
            <span class="cv-lab">Opacity</span>
            <input v-model.number="selectedLayer.opacity" type="range" min="0.05" max="1" step="0.05" class="cv-range" />
          </template>
          <span class="cv-sep"></span>
          <div class="cv-dd">
            <button class="cv-tbtn" :class="{ on: menu === 'position' }" title="Position" @click="toggleMenu('position')"><i class="fas fa-th-large"></i></button>
            <div v-if="menu === 'position'" class="cv-pop cv-pop-end" @click.stop>
              <button @click="nudgeAlign('left')">Align left</button>
              <button @click="nudgeAlign('center')">Align center</button>
              <button @click="nudgeAlign('right')">Align right</button>
              <button @click="nudgeAlign('top')">Align top</button>
              <button @click="nudgeAlign('middle')">Align middle</button>
              <button @click="nudgeAlign('bottom')">Align bottom</button>
              <hr />
              <button @click="moveLayer(1)">Bring forward</button>
              <button @click="moveLayer(-1)">Send backward</button>
            </div>
          </div>
          <button class="cv-tbtn" title="Duplicate" @click="duplicateSelected"><i class="fas fa-copy"></i></button>
          <button class="cv-tbtn" title="Delete" @click="deleteSelected"><i class="fas fa-trash"></i></button>
        </div>

        <div ref="stageWrap" class="cv-stage" @mousedown="onStageDown" @wheel.prevent="onWheel">
          <div
            class="cv-page"
            :style="pageBoxStyle"
            @mousedown.stop="onPageDown"
          >
            <div class="cv-bg" :style="pageBgStyle"></div>
            <div
              v-for="layer in page.layers"
              :key="layer.id"
              class="cv-el"
              :class="{ on: isSelected(layer.id), edit: editingId === layer.id, lock: layer.locked }"
              :data-id="layer.id"
              :style="elStyle(layer)"
              @mousedown.stop="onLayerDown($event, layer)"
              @dblclick.stop="onLayerDbl(layer)"
              @contextmenu.prevent.stop="onLayerContext($event, layer)"
            >
              <template v-if="layer.type === 'text'">
                <div
                  v-if="editingId === layer.id"
                  :key="'edit-' + layer.id"
                  class="cv-txt cv-txt-edit"
                  :style="{ whiteSpace: layer.nowrap ? 'pre' : 'pre-wrap' }"
                  contenteditable="true"
                  spellcheck="false"
                  @mousedown.stop
                  @keydown.stop
                  @blur="stopEdit($event, layer)"
                ></div>
                <div v-else :key="'view-' + layer.id" class="cv-txt" :style="{ whiteSpace: layer.nowrap ? 'pre' : 'pre-wrap' }"><span class="cv-txt-inner">{{ layer.text }}</span></div>
              </template>
              <video
                v-else-if="layer.type === 'video'"
                class="cv-vid"
                :src="layer.src"
                :muted="layer.muted !== false"
                :loop="layer.loop !== false"
                :autoplay="!layer.paused"
                playsinline
                preload="auto"
                :style="mediaStyle(layer)"
                @play="layer.paused = false"
                @pause="layer.paused = true"
                @timeupdate="onVideoTime(layer, $event)"
                @loadedmetadata="onVideoMeta(layer, $event)"
              />
              <svg
                v-else-if="layer.type === 'image' && layer.path"
                class="cv-path"
                :viewBox="`0 0 ${layer.viewBoxW || 64} ${layer.viewBoxH || 64}`"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <clipPath :id="'cvclip-' + layer.id">
                    <path :d="layer.path" />
                  </clipPath>
                </defs>
                <image
                  :href="layer.src"
                  :x="layer.imageW ? layer.imageX : 0"
                  :y="layer.imageW ? layer.imageY : 0"
                  :width="layer.imageW || layer.viewBoxW || 64"
                  :height="layer.imageH || layer.viewBoxH || 64"
                  :preserveAspectRatio="layer.objectFit === 'cover' || !layer.imageW ? 'xMidYMid slice' : 'none'"
                  :clip-path="'url(#cvclip-' + layer.id + ')'"
                />
              </svg>
              <img v-else-if="layer.type === 'image'" :src="layer.src" alt="" draggable="false" :style="mediaStyle(layer)" />
              <div v-else-if="layer.type === 'icon'" class="cv-icn">
                <i :class="['fas', `fa-${layer.icon}`]"></i>
              </div>
              <svg
                v-else-if="layer.type === 'path'"
                class="cv-path"
                :viewBox="`0 0 ${layer.viewBoxW || 64} ${layer.viewBoxH || 64}`"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path :d="layer.path" :fill="layer.fill || '#000'" />
              </svg>
              <div v-else class="cv-shp" :style="shapeEl(layer)"></div>

              <template v-if="isSelected(layer.id) && editingId !== layer.id && !layer.locked">
                <span class="h nw" @mousedown.stop.prevent="beginResize($event, layer, 'nw')"></span>
                <span class="h n" @mousedown.stop.prevent="beginResize($event, layer, 'n')"></span>
                <span class="h ne" @mousedown.stop.prevent="beginResize($event, layer, 'ne')"></span>
                <span class="h w" @mousedown.stop.prevent="beginResize($event, layer, 'w')"></span>
                <span class="h e" @mousedown.stop.prevent="beginResize($event, layer, 'e')"></span>
                <span class="h sw" @mousedown.stop.prevent="beginResize($event, layer, 'sw')"></span>
                <span class="h s" @mousedown.stop.prevent="beginResize($event, layer, 's')"></span>
                <span class="h se" @mousedown.stop.prevent="beginResize($event, layer, 'se')"></span>
                <span class="rot" @mousedown.stop.prevent="beginRotate($event, layer)"><i class="fas fa-redo"></i></span>
              </template>
            </div>
          </div>
        </div>

        <div class="cv-pages">
          <div
            v-for="(p, i) in pages"
            :key="p.id"
            class="cv-page-thumb"
            :class="{ on: i === pageIndex }"
          >
            <div class="cv-page-frame">
              <button
                type="button"
                class="cv-page-preview"
                :style="pageThumbFrameStyle(p)"
                :title="'Page ' + (i + 1)"
                @click="selectPage(i)"
              >
                <DesignPreview :page-data="p" :max-size="pageThumbMaxSize(p)" flat />
              </button>
              <button
                v-if="pages.length > 1"
                type="button"
                class="cv-page-del"
                title="Delete page"
                @click.stop="removePage(i)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <span class="cv-page-num">{{ i + 1 }}</span>
          </div>
          <button class="cv-add-page" title="Add page" @click="addPage"><i class="fas fa-plus"></i></button>
        </div>
      </div>
    </div>

    <div v-if="menu === 'share'" class="cv-modal" @click.self="menu = null">
      <div class="cv-sheet" @click.stop>
        <h4>Share</h4>
        <p class="cv-hint">Send this design to the approval queue.</p>
        <label>Account</label>
        <select v-model="accountId" class="cv-input">
          <option value="">Select account</option>
          <option v-for="acc in accounts" :key="acc.id" :value="acc.id">@{{ acc.username }}</option>
        </select>
        <label>Post type</label>
        <select v-model="postType" class="cv-input">
          <option value="feed">Feed</option>
          <option value="story">Story</option>
          <option value="reel">Reel</option>
        </select>
        <label>Caption</label>
        <textarea v-model="caption" class="cv-input" rows="4" placeholder="Write a caption…"></textarea>
        <button class="cv-share block" :disabled="exporting" @click="onShareApprove">{{ exporting ? 'Sending…' : 'Send to approval' }}</button>
      </div>
    </div>

    <div v-if="menu === 'download'" class="cv-modal" @click.self="menu = null">
      <div class="cv-sheet" @click.stop>
        <h4>Download</h4>
        <label>File type</label>
        <select v-model="exportType" class="cv-input">
          <option value="png">PNG</option>
          <option value="jpeg">JPG</option>
        </select>
        <button class="cv-share block" :disabled="exporting" @click="onDownload">{{ exporting ? 'Rendering…' : 'Download' }}</button>
      </div>
    </div>

    <div v-if="menu === 'resize'" class="cv-modal" @click.self="menu = null">
      <div class="cv-sheet wide" @click.stop>
        <h4>Magic Resize</h4>
        <div class="cv-size-grid">
          <button v-for="s in sizes" :key="s.id" @click="resizeTo(s.w, s.h); menu = null">
            <strong>{{ s.name }}</strong>
            <span>{{ s.w }} × {{ s.h }}</span>
          </button>
        </div>
        <div class="cv-custom">
          <input v-model.number="customW" type="number" min="100" class="cv-input" />
          <span>×</span>
          <input v-model.number="customH" type="number" min="100" class="cv-input" />
          <button class="cv-share" @click="resizeTo(customW, customH); menu = null">Resize</button>
        </div>
      </div>
    </div>

    <div v-if="canvaReportOpen" class="cv-modal" @click.self="canvaReportOpen = false">
      <div class="cv-sheet" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">{{ $t('designStudio.report_canva_import') }}</h4>
          <button type="button" class="btn btn-sm btn-link" :disabled="canvaReportSending" @click="canvaReportOpen = false">×</button>
        </div>
        <p class="small text-muted">{{ $t('designStudio.report_canva_hint') }}</p>
        <form @submit.prevent="submitCanvaReport">
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('designHub.canva_link') }}</label>
            <input
              v-model="canvaReportForm.url"
              class="form-control form-control-sm cv-input"
              required
              :placeholder="$t('designHub.canva_url_placeholder')"
              :disabled="canvaReportSending"
            />
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('designStudio.report_message') }}</label>
            <textarea
              v-model="canvaReportForm.message"
              class="form-control form-control-sm"
              rows="4"
              required
              maxlength="4000"
              :placeholder="$t('designStudio.report_message_placeholder')"
              :disabled="canvaReportSending"
            />
          </div>
          <div class="text-right">
            <button type="button" class="btn btn-sm btn-outline-secondary mr-2" :disabled="canvaReportSending" @click="canvaReportOpen = false">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" class="btn btn-sm btn-primary" :disabled="canvaReportSending || !canvaReportForm.url.trim() || !canvaReportForm.message.trim()">
              {{ canvaReportSending ? $t('designStudio.report_sending') : $t('designStudio.send_report') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="ctx.show" class="cv-ctx" :style="{ left: ctx.x + 'px', top: ctx.y + 'px' }" @click.stop>
      <button @click="duplicateSelected">{{ $t('designStudio.duplicate') }}</button>
      <button @click="copySelected">{{ $t('designStudio.copy') }}</button>
      <button @click="pasteClipboard">{{ $t('designStudio.paste') }}</button>
      <button v-if="selectedLayer && (selectedLayer.type === 'image' || selectedLayer.type === 'video')" @click="setSelectedAsBg">{{ $t('designStudio.set_background') }}</button>
      <button @click="moveLayer(1)">{{ $t('designStudio.bring_forward') }}</button>
      <button @click="moveLayer(-1)">{{ $t('designStudio.send_backward') }}</button>
      <button v-if="selectedLayer" @click="selectedLayer.locked = !selectedLayer.locked; ctx.show = false">{{ selectedLayer.locked ? $t('designStudio.unlock') : $t('designStudio.lock') }}</button>
      <button class="danger" @click="deleteSelected">{{ $t('common.delete') }}</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  uid, clone, FONTS, SIZE_PRESETS, PALETTES, STICKERS, SHAPES,
  TEXT_PRESETS, TEXT_COMBOS, STARTER_TEMPLATES,
  textLayer, shapeLayer, iconLayer, imageLayer, videoLayer, blankPage, applyTemplate,
  parseDocument, layerCssFilter, iconUnicode, registerFontFace, mergeFontFamilies,
  applyLibraryToPages, isVideoSrc, materializeBackground,
} from './studio/presets'
import DesignPreview from './studio/DesignPreview.vue'

export default {
  components: { DesignPreview },
  data() {
    return {
      fonts: FONTS,
      sizes: SIZE_PRESETS,
      palettes: PALETTES,
      stickers: STICKERS,
      shapes: SHAPES,
      textPresets: TEXT_PRESETS,
      textCombos: TEXT_COMBOS,
      leftTab: 'design',
      menu: null,
      query: '',
      name: '',
      templateId: null,
      pages: [blankPage()],
      pageIndex: 0,
      selectedIds: [],
      editingId: null,
      zoom: 40,
      saving: false,
      exporting: false,
      aiLoading: false,
      dirty: false,
      aiPrompt: '',
      accountId: '',
      caption: '',
      postType: 'feed',
      exportType: 'png',
      customW: 1080,
      customH: 1080,
      history: [],
      historyIndex: -1,
      clipboard: [],
      interact: null,
      uploads: [],
      ctx: { show: false, x: 0, y: 0 },
      saveTimer: null,
      fontQuery: '',
      fontHi: 0,
      fontLibrary: [],
      canvaSourceUrl: '',
      canvaReportOpen: false,
      canvaReportSending: false,
      canvaReportForm: { url: '', message: '' },
    }
  },
  computed: {
    ...mapGetters('ai', ['templates']),
    ...mapGetters('app', ['accounts']),
    page() {
      return this.pages[this.pageIndex] || this.pages[0]
    },
    scale() {
      return this.zoom / 100
    },
    selectedLayer() {
      const id = this.selectedIds[this.selectedIds.length - 1]
      if (!id || !this.page) return null
      return this.page.layers.find((l) => l.id === id) || null
    },
    rails() {
      return [
        { id: 'design', icon: 'fa-th-large', label: this.$t('designStudio.design') },
        { id: 'elements', icon: 'fa-shapes', label: this.$t('designStudio.elements') },
        { id: 'text', icon: 'fa-font', label: this.$t('designStudio.text') },
        { id: 'uploads', icon: 'fa-cloud-upload-alt', label: this.$t('designStudio.uploads') },
      ]
    },
    canUndo() { return this.historyIndex > 0 },
    canRedo() { return this.historyIndex >= 0 && this.historyIndex < this.history.length - 1 },
    currentSizeId() {
      if (!this.page) return 'custom'
      const match = this.sizes.find((s) => s.w === this.page.width && s.h === this.page.height)
      return match ? match.id : 'custom'
    },
    panelTitle() {
      const t = this.rails.find((r) => r.id === this.leftTab)
      return t ? t.label : ''
    },
    searchPlaceholder() {
      if (this.leftTab === 'design') return this.$t('designStudio.search_templates')
      if (this.leftTab === 'text') return this.$t('designStudio.search_fonts')
      if (this.leftTab === 'elements') return this.$t('designStudio.search_elements')
      return this.$t('common.search')
    },
    filteredTemplates() {
      const q = this.query.toLowerCase().trim()
      if (!q) return STARTER_TEMPLATES
      return STARTER_TEMPLATES.filter((t) => (
        t.name.toLowerCase().indexOf(q) !== -1 || t.category.toLowerCase().indexOf(q) !== -1
      ))
    },
    pageBoxStyle() {
      if (!this.page) return {}
      return {
        width: `${this.page.width * this.scale}px`,
        height: `${this.page.height * this.scale}px`,
      }
    },
    pageBgStyle() {
      if (!this.page) return {}
      return {
        backgroundColor: this.page.backgroundColor || '#fff',
        backgroundImage: this.page.backgroundUrl ? `url(${this.page.backgroundUrl})` : 'none',
      }
    },
    hexFill() {
      const f = this.selectedLayer && this.selectedLayer.fill
      return f && f !== 'transparent' ? f : '#8b3dff'
    },
    hexStroke() {
      const f = this.selectedLayer && this.selectedLayer.stroke
      return f && f !== 'transparent' ? f : '#0d0c22'
    },
    alignIcon() {
      const a = this.selectedLayer && this.selectedLayer.align
      if (a === 'right') return 'fas fa-align-right'
      if (a === 'center') return 'fas fa-align-center'
      return 'fas fa-align-left'
    },
    filteredFonts() {
      const q = (this.fontQuery || '').toLowerCase().trim()
      if (!q) return this.fonts
      return this.fonts.filter((f) => f.toLowerCase().indexOf(q) !== -1)
    },
  },
  watch: {
    pages: {
      deep: true,
      handler() {
        this.dirty = true
        this.scheduleSave()
      },
    },
    name() { this.dirty = true; this.scheduleSave() },
    '$route.params.id'(id) {
      if (id && String(id) !== String(this.templateId)) this.loadTemplate(id)
    },
    fontQuery() {
      this.fontHi = 0
      this.scrollFontHi()
    },
  },
  async created() {
    await this.fetchAccounts()
    await this.fetchTemplates()
    await this.loadStudioFonts()
    if (this.accounts.length) this.accountId = this.accounts[0].id
    const id = this.$route.params.id
    if (id) {
      await this.loadTemplate(id)
    } else {
      this.$router.replace('/ai/studio')
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKey)
    window.addEventListener('mousemove', this.onMove)
    window.addEventListener('mouseup', this.onUp)
    window.addEventListener('resize', this.fitToScreen)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('mousemove', this.onMove)
    window.removeEventListener('mouseup', this.onUp)
    window.removeEventListener('resize', this.fitToScreen)
    if (this.saveTimer) clearTimeout(this.saveTimer)
  },
  methods: {
    ...mapActions('ai', [
      'fetchTemplates', 'fetchTemplate', 'updateTemplate', 'deleteTemplate', 'reportCanvaImport',
      'generateLayerText', 'studioCreatePost', 'fetchStudioFonts',
    ]),
    ...mapActions('app', ['fetchAccounts']),
    ...mapActions('posts', ['uploadMedia']),
    snapshot() {
      return JSON.stringify({ name: this.name, pages: this.pages, pageIndex: this.pageIndex })
    },
    pushHistory(reset) {
      const snap = this.snapshot()
      if (reset) {
        this.history = [snap]
        this.historyIndex = 0
        return
      }
      if (this.history[this.historyIndex] === snap) return
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(snap)
      if (this.history.length > 60) this.history.shift()
      this.historyIndex = this.history.length - 1
      this.dirty = true
    },
    restore(snap) {
      const data = JSON.parse(snap)
      this.name = data.name
      this.pages = (data.pages || []).map((p) => materializeBackground(p))
      this.pageIndex = data.pageIndex
      this.selectedIds = []
      this.dirty = true
    },
    undo() {
      if (!this.canUndo) return
      this.historyIndex -= 1
      this.restore(this.history[this.historyIndex])
    },
    redo() {
      if (!this.canRedo) return
      this.historyIndex += 1
      this.restore(this.history[this.historyIndex])
    },
    scheduleSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        if (this.dirty && this.templateId) this.onSave()
      }, 800)
    },
    toggleMenu(id) {
      this.menu = this.menu === id ? null : id
      this.ctx.show = false
      if (this.menu !== 'font') {
        this.fontQuery = ''
        return
      }
      const current = this.selectedLayer && this.selectedLayer.fontFamily
      this.fontHi = Math.max(0, this.fonts.indexOf(current))
      this.$nextTick(() => {
        if (this.$refs.fontSearch) this.$refs.fontSearch.focus()
        this.scrollFontHi()
      })
    },
    closeFontMenu() {
      this.menu = null
      this.fontQuery = ''
    },
    moveFontHi(step) {
      const n = this.filteredFonts.length
      if (!n) return
      this.fontHi = (this.fontHi + step + n) % n
      this.scrollFontHi()
    },
    pickHighlightedFont() {
      const f = this.filteredFonts[this.fontHi]
      if (f) this.pickFont(f)
    },
    pickFont(f) {
      this.applyFont(f)
      this.closeFontMenu()
    },
    scrollFontHi() {
      this.$nextTick(() => {
        const list = this.$refs.fontList
        if (!list) return
        const on = list.querySelector('.hi')
        if (on && on.scrollIntoView) on.scrollIntoView({ block: 'nearest' })
      })
    },
    toggleTab(id) {
      this.leftTab = this.leftTab === id ? null : id
      this.query = ''
    },
    onSizeSelect(e) {
      const id = e.target.value
      const preset = this.sizes.find((s) => s.id === id)
      if (preset) {
        this.resizeTo(preset.w, preset.h)
        return
      }
      this.customW = this.page.width
      this.customH = this.page.height
      this.menu = 'resize'
    },
    onRootClick() {
      this.menu = null
      this.fontQuery = ''
      this.ctx.show = false
    },
    onRootContext() {
      this.ctx.show = false
    },
    isSelected(id) {
      return this.selectedIds.indexOf(id) !== -1
    },
    fitToScreen() {
      this.$nextTick(() => {
        const wrap = this.$refs.stageWrap
        if (!wrap || !this.page) return
        const sx = (wrap.clientWidth - 96) / this.page.width
        const sy = (wrap.clientHeight - 96) / this.page.height
        this.zoom = Math.max(10, Math.min(150, Math.floor(Math.min(sx, sy) * 100)))
      })
    },
    elStyle(layer) {
      const s = this.scale
      const style = {
        left: `${layer.x * s}px`,
        top: `${layer.y * s}px`,
        width: `${layer.width * s}px`,
        height: `${(layer.height || layer.fontSize || 40) * s}px`,
        opacity: layer.opacity,
        transform: `rotate(${layer.rotation || 0}deg)`,
        transformOrigin: (layer.rotOx || layer.rotOy) ? `${layer.rotOx * s}px ${layer.rotOy * s}px` : 'center center',
        zIndex: this.page.layers.indexOf(layer) + 1,
        overflow: (layer.type === 'image' || layer.type === 'video') && (!layer.shadow || layer.objectFit === 'cover') ? 'hidden' : undefined,
        filter: layer.shadow && layer.type !== 'text' ? 'drop-shadow(6px 10px 12px rgba(0,0,0,0.45))' : undefined,
      }
      if (layer.type === 'icon') {
        style.color = layer.color || '#0d0c22'
        style.fontSize = `${Math.min(layer.width, layer.height || layer.width) * s * 0.82}px`
        style.display = 'flex'
        style.alignItems = 'center'
        style.justifyContent = 'center'
      } else if (layer.type === 'text') {
        const align = layer.align || 'center'
        const vAlign = layer.verticalAlign || 'center'
        style.display = 'flex'
        style.flexDirection = 'column'
        style.justifyContent = vAlign === 'top' || vAlign === 'start' ? 'flex-start' : (vAlign === 'bottom' || vAlign === 'end' ? 'flex-end' : 'center')
        style.alignItems = 'stretch'
        style.overflow = 'visible'
        style.fontFamily = `'${layer.fontFamily || 'Montserrat'}', sans-serif`
        style.fontSize = `${(layer.fontSize || 48) * s}px`
        style.fontWeight = layer.fontWeight || 700
        style.fontStyle = layer.fontStyle || 'normal'
        style.fontSynthesis = 'none'
        style.color = layer.color || '#0d0c22'
        style.textAlign = align
        style.letterSpacing = `${(layer.letterSpacing || 0) * s}px`
        style.lineHeight = layer.lineHeight || 1.12
        style.whiteSpace = layer.nowrap ? 'pre' : 'pre-wrap'
        style.textTransform = layer.uppercase ? 'uppercase' : 'none'
        style.textDecoration = layer.underline ? 'underline' : 'none'
        style.textShadow = layer.shadow ? `0 ${4 * s}px ${12 * s}px rgba(0,0,0,0.35)` : 'none'
        style.webkitTextStroke = layer.outline ? `${(layer.outlineWidth || 4) * s * 0.35}px ${layer.outlineColor || '#000'}` : '0'
      }
      return style
    },
    shapeEl(layer) {
      const style = {
        background: layer.fill,
        borderRadius: layer.shape === 'ellipse' ? '50%' : `${(layer.radius || 0) * this.scale}px`,
        border: layer.strokeWidth ? `${layer.strokeWidth * this.scale}px solid ${layer.stroke}` : 'none',
      }
      if (layer.shape === 'triangle') style.clipPath = 'polygon(50% 0%, 100% 100%, 0% 100%)'
      if (layer.shape === 'star') style.clipPath = 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
      if (layer.shape === 'line') {
        style.border = 'none'
        style.background = layer.fill
        style.borderRadius = '99px'
      }
      return style
    },
    shapePreview(s) {
      const style = {
        background: s.fill === 'transparent' ? 'transparent' : s.fill,
        border: s.strokeWidth ? `3px solid ${s.stroke}` : 'none',
        borderRadius: s.shape === 'ellipse' ? '50%' : (s.radius ? '10px' : '2px'),
      }
      if (s.shape === 'triangle') style.clipPath = 'polygon(50% 0%, 100% 100%, 0% 100%)'
      if (s.shape === 'star') style.clipPath = 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
      if (s.shape === 'line') {
        style.height = '4px'
        style.width = '28px'
        style.marginTop = '14px'
        style.background = s.fill
      }
      return style
    },
    imageFilter(layer) {
      return layerCssFilter(layer)
    },
    mediaStyle(layer) {
      const style = { filter: this.imageFilter(layer) }
      if (layer.objectFit === 'cover') style.objectFit = 'cover'
      if (layer.scale > 1.02) style.transform = `scale(${layer.scale})`
      return style
    },
    isVideoSrc,
    videoEl(layer) {
      if (!layer || !this.$el) return null
      const host = this.$el.querySelector(`.cv-el[data-id="${layer.id}"] video`)
      return host || null
    },
    toggleVideoPlay(layer) {
      const el = this.videoEl(layer)
      if (!el) return
      if (el.paused) {
        el.play().catch(() => {})
        this.$set(layer, 'paused', false)
      } else {
        el.pause()
        this.$set(layer, 'paused', true)
      }
    },
    toggleVideoMute(layer) {
      layer.muted = layer.muted === false
      const el = this.videoEl(layer)
      if (el) el.muted = layer.muted !== false
      this.pushHistory()
    },
    seekVideo(layer, e) {
      const el = this.videoEl(layer)
      const t = Number(e.target.value)
      this.$set(layer, '_time', t)
      if (el) el.currentTime = t
    },
    onVideoTime(layer, e) {
      const el = e.target
      this.$set(layer, '_time', el.currentTime || 0)
      this.$set(layer, '_dur', el.duration || 0)
    },
    onVideoMeta(layer, e) {
      const el = e.target
      this.$set(layer, '_dur', el.duration || 0)
      if (!layer.paused) el.play().catch(() => {})
    },
    selectOnly(id) {
      this.selectedIds = [id]
    },
    onPageDown() {
      if (this.editingId) return
      this.selectedIds = []
    },
    onStageDown() {
      this.selectedIds = []
      this.editingId = null
    },
    onLayerDown(e, layer) {
      if (layer.locked) {
        this.selectOnly(layer.id)
        return
      }
      if (e.shiftKey) {
        if (this.isSelected(layer.id)) this.selectedIds = this.selectedIds.filter((id) => id !== layer.id)
        else this.selectedIds.push(layer.id)
      } else if (!this.isSelected(layer.id)) {
        this.selectOnly(layer.id)
      }
      if (this.editingId && this.editingId !== layer.id) this.editingId = null
      if (this.editingId === layer.id) return
      const orig = {}
      this.selectedIds.forEach((id) => {
        const l = this.page.layers.find((x) => x.id === id)
        if (l) orig[id] = { x: l.x, y: l.y, width: l.width, height: l.height, rotation: l.rotation || 0, fontSize: l.fontSize }
      })
      this.interact = { mode: 'move', startX: e.clientX, startY: e.clientY, orig }
    },
    beginResize(e, layer, handle) {
      this.selectOnly(layer.id)
      this.interact = {
        mode: 'resize',
        handle,
        startX: e.clientX,
        startY: e.clientY,
        orig: { [layer.id]: { x: layer.x, y: layer.y, width: layer.width, height: layer.height, fontSize: layer.fontSize } },
      }
    },
    beginRotate(e, layer) {
      this.selectOnly(layer.id)
      const el = e.currentTarget.parentElement
      const rect = el.getBoundingClientRect()
      this.interact = {
        mode: 'rotate',
        cx: rect.left + rect.width / 2,
        cy: rect.top + rect.height / 2,
        orig: { [layer.id]: { rotation: layer.rotation || 0 } },
        startAng: Math.atan2(e.clientY - (rect.top + rect.height / 2), e.clientX - (rect.left + rect.width / 2)),
      }
    },
    onMove(e) {
      if (!this.interact) return
      const s = this.scale
      if (this.interact.mode === 'move') {
        const dx = (e.clientX - this.interact.startX) / s
        const dy = (e.clientY - this.interact.startY) / s
        Object.keys(this.interact.orig).forEach((id) => {
          const l = this.page.layers.find((x) => x.id === id)
          if (!l) return
          l.x = Math.round(this.interact.orig[id].x + dx)
          l.y = Math.round(this.interact.orig[id].y + dy)
        })
      } else if (this.interact.mode === 'resize') {
        const id = Object.keys(this.interact.orig)[0]
        const l = this.page.layers.find((x) => x.id === id)
        const o = this.interact.orig[id]
        if (!l) return
        const dx = (e.clientX - this.interact.startX) / s
        const dy = (e.clientY - this.interact.startY) / s
        const h = this.interact.handle
        let x = o.x
        let y = o.y
        let w = o.width
        let ht = o.height
        if (h.indexOf('e') !== -1) w = Math.max(24, o.width + dx)
        if (h.indexOf('s') !== -1) ht = Math.max(24, o.height + dy)
        if (h.indexOf('w') !== -1) {
          w = Math.max(24, o.width - dx)
          x = o.x + o.width - w
        }
        if (h.indexOf('n') !== -1) {
          ht = Math.max(24, o.height - dy)
          y = o.y + o.height - ht
        }
        l.x = Math.round(x)
        l.y = Math.round(y)
        l.width = Math.round(w)
        l.height = Math.round(ht)
        if (l.type === 'text' && o.fontSize) {
          l.fontSize = Math.max(8, Math.round(o.fontSize * (ht / o.height)))
        }
      } else if (this.interact.mode === 'rotate') {
        const id = Object.keys(this.interact.orig)[0]
        const l = this.page.layers.find((x) => x.id === id)
        if (!l) return
        const ang = Math.atan2(e.clientY - this.interact.cy, e.clientX - this.interact.cx)
        let deg = ((ang - this.interact.startAng) * 180) / Math.PI + (this.interact.orig[id].rotation || 0)
        if (e.shiftKey) deg = Math.round(deg / 15) * 15
        l.rotation = Math.round(deg)
      }
    },
    onUp() {
      if (this.interact) {
        this.interact = null
        this.pushHistory()
      }
    },
    onLayerDbl(layer) {
      if (layer.type === 'video') {
        this.toggleVideoPlay(layer)
        return
      }
      if (layer.type !== 'text' || layer.locked) return
      this.editingId = layer.id
      this.$nextTick(() => {
        const el = this.$el.querySelector('.cv-txt-edit')
        if (!el) return
        el.textContent = layer.text || ''
        el.focus()
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      })
    },
    stopEdit(e, layer) {
      if (layer && e && e.target) {
        layer.text = (e.target.innerText || '').replace(/\n$/, '')
      }
      this.editingId = null
      this.pushHistory()
    },
    onLayerContext(e, layer) {
      if (!this.isSelected(layer.id)) this.selectOnly(layer.id)
      this.ctx = { show: true, x: e.clientX, y: e.clientY }
    },
    onWheel(e) {
      if (!(e.ctrlKey || e.metaKey)) return
      const next = this.zoom + (e.deltaY > 0 ? -5 : 5)
      this.zoom = Math.max(10, Math.min(250, next))
    },
    onKey(e) {
      const tag = (e.target && e.target.tagName) || ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || this.editingId) return
      const meta = e.metaKey || e.ctrlKey
      if (meta && e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.undo(); return }
      if (meta && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); this.redo(); return }
      if (meta && e.key === 'd') { e.preventDefault(); this.duplicateSelected(); return }
      if (meta && e.key === 'c') { this.copySelected(); return }
      if (meta && e.key === 'v') { this.pasteClipboard(); return }
      if (meta && e.key === 's') { e.preventDefault(); this.onSave(); return }
      if (e.key === 'Escape') { this.selectedIds = []; this.menu = null; return }
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedIds.length) {
        e.preventDefault()
        this.deleteSelected()
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) !== -1 && this.selectedLayer) {
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        this.selectedIds.forEach((id) => {
          const l = this.page.layers.find((x) => x.id === id)
          if (!l) return
          if (e.key === 'ArrowLeft') l.x -= step
          if (e.key === 'ArrowRight') l.x += step
          if (e.key === 'ArrowUp') l.y -= step
          if (e.key === 'ArrowDown') l.y += step
        })
        this.pushHistory()
      }
    },
    async loadStudioFonts() {
      try {
        const fonts = await this.fetchStudioFonts()
        this.fontLibrary = fonts || []
        this.fontLibrary.forEach((font) => registerFontFace(font))
        this.fonts = mergeFontFamilies(FONTS, this.fontLibrary)
      } catch (e) {
        this.fontLibrary = []
        this.fonts = FONTS.slice()
      }
    },
    findLibraryFont(family, weight, style) {
      const w = String(weight || '400')
      const s = style || 'normal'
      const exact = this.fontLibrary.find((f) => (
        f.family === family
        && String(f.weight || '400') === w
        && (f.style || 'normal') === s
      ))
      if (exact) return exact
      return this.fontLibrary.find((f) => f.family === family)
    },
    fontPatch(family, extra = {}) {
      const face = this.findLibraryFont(family, extra.fontWeight, extra.fontStyle)
      const patch = { fontFamily: family, ...extra }
      if (face) {
        registerFontFace(face)
        patch.fontUrl = face.url
        patch.fontWeight = face.weight || extra.fontWeight || '400'
        patch.fontStyle = face.style || extra.fontStyle || 'normal'
      }
      return patch
    },
    addText(partial) {
      const patch = partial.fontFamily
        ? this.fontPatch(partial.fontFamily, partial)
        : partial
      const layer = textLayer({
        x: Math.round(this.page.width * 0.08),
        width: Math.round(this.page.width * 0.84),
        y: Math.round(this.page.height * 0.4),
        ...patch,
      })
      this.page.layers.push(layer)
      this.selectOnly(layer.id)
      this.pushHistory()
    },
    addShape(s) {
      const size = Math.round(this.page.width * 0.36)
      const layer = shapeLayer({
        ...s,
        x: Math.round((this.page.width - size) / 2),
        y: Math.round((this.page.height - size) / 2),
        width: size,
        height: s.shape === 'line' ? 16 : size,
      })
      this.page.layers.push(layer)
      this.selectOnly(layer.id)
      this.pushHistory()
    },
    addSticker(sticker) {
      const size = 120
      const layer = iconLayer({
        icon: sticker.icon,
        width: size,
        height: size,
        y: Math.round(this.page.height / 2 - size / 2),
        x: Math.round(this.page.width / 2 - size / 2),
        color: '#0d0c22',
      })
      this.page.layers.push(layer)
      this.selectOnly(layer.id)
      this.pushHistory()
    },
    addImage(src, asBg) {
      this.addMedia(src, asBg)
    },
    addMedia(src, asBg) {
      const factory = isVideoSrc(src) ? videoLayer : imageLayer
      if (asBg) {
        const layer = factory(src, { x: 0, y: 0, width: this.page.width, height: this.page.height })
        this.page.layers.unshift(layer)
        this.selectOnly(layer.id)
        this.pushHistory()
        return
      }
      const size = Math.round(this.page.width * 0.7)
      const layer = factory(src, {
        x: Math.round((this.page.width - size) / 2),
        y: Math.round((this.page.height - size) / 2),
        width: size,
        height: size,
      })
      this.page.layers.push(layer)
      this.selectOnly(layer.id)
      this.pushHistory()
    },
    addCombo(combo) {
      combo.layers.forEach((partial, i) => {
        const layer = textLayer({
          x: Math.round(this.page.width * 0.08),
          width: Math.round(this.page.width * 0.84),
          ...partial,
          y: Math.round(this.page.height * 0.35) + i * 120,
        })
        this.page.layers.push(layer)
        if (i === combo.layers.length - 1) this.selectOnly(layer.id)
      })
      this.pushHistory()
    },
    palettePreviewStyle(p) {
      const colors = p.colors || ['#ffffff']
      if (colors.length < 2) return { background: colors[0] }
      return {
        background: `linear-gradient(145deg, ${colors[0]} 0%, ${colors[1]} 52%, ${colors[0]} 100%)`,
      }
    },
    paletteTextColor(p) {
      const colors = p.colors || []
      if (colors[2]) return colors[2]
      if (colors[1]) return colors[1]
      return '#ffffff'
    },
    applyPalette(p) {
      this.page.backgroundColor = p.colors[0]
      this.pushHistory()
    },
    applyFont(f) {
      const patch = this.fontPatch(f, { text: f })
      if (this.selectedLayer && this.selectedLayer.type === 'text') {
        Object.assign(this.selectedLayer, patch)
        this.pushHistory()
      } else {
        this.addText(patch)
      }
    },
    async onUploadFiles(e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      for (let i = 0; i < files.length; i += 1) {
        try {
          const data = await this.uploadMedia(files[i])
          this.uploads.unshift(data.url)
          this.addImage(data.url)
        } catch (err) {
          this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Upload failed')
        }
      }
    },
    useStarter(tpl) {
      const w = this.page.width
      const h = this.page.height
      const next = applyTemplate(tpl)
      const sx = w / next.width
      const sy = h / next.height
      next.layers.forEach((l) => {
        l.x = Math.round(l.x * sx)
        l.y = Math.round(l.y * sy)
        l.width = Math.round(l.width * sx)
        l.height = Math.round(l.height * sy)
        if (l.fontSize) l.fontSize = Math.round(l.fontSize * Math.min(sx, sy))
      })
      next.width = w
      next.height = h
      this.$set(this.pages, this.pageIndex, next)
      this.selectedIds = []
      this.pushHistory()
    },
    addPage() {
      this.pages.push(blankPage(this.page.width, this.page.height, this.page.backgroundColor))
      this.pageIndex = this.pages.length - 1
      this.selectedIds = []
      this.editingId = null
      this.pushHistory()
      this.$nextTick(() => this.fitToScreen())
    },
    selectPage(i) {
      this.pageIndex = i
      this.selectedIds = []
      this.editingId = null
      this.$nextTick(() => this.fitToScreen())
    },
    pageThumbFrameStyle(p) {
      const w = Math.max(1, p.width || 1080)
      const h = Math.max(1, p.height || 1080)
      const maxH = 56
      const maxW = 44
      let tw = maxW
      let th = Math.round((maxW * h) / w)
      if (th > maxH) {
        th = maxH
        tw = Math.round((maxH * w) / h)
      }
      return { width: `${tw}px`, height: `${th}px` }
    },
    pageThumbMaxSize(p) {
      const w = Math.max(1, p.width || 1080)
      const h = Math.max(1, p.height || 1080)
      const maxH = 56
      const maxW = 44
      let tw = maxW
      let th = Math.round((maxW * h) / w)
      if (th > maxH) {
        th = maxH
        tw = Math.round((maxH * w) / h)
      }
      return Math.max(tw, th)
    },
    removePage(i) {
      if (this.pages.length <= 1) return
      this.pages.splice(i, 1)
      if (this.pageIndex >= this.pages.length) this.pageIndex = this.pages.length - 1
      else if (this.pageIndex > i) this.pageIndex -= 1
      this.selectedIds = []
      this.editingId = null
      this.pushHistory()
      this.$nextTick(() => this.fitToScreen())
    },
    duplicateSelected() {
      const copies = this.selectedIds.map((id) => {
        const src = this.page.layers.find((l) => l.id === id)
        if (!src) return null
        const copy = clone(src)
        copy.id = uid()
        copy.x += 24
        copy.y += 24
        this.page.layers.push(copy)
        return copy.id
      }).filter(Boolean)
      this.selectedIds = copies
      this.ctx.show = false
      this.pushHistory()
    },
    copySelected() {
      this.clipboard = this.selectedIds.map((id) => clone(this.page.layers.find((l) => l.id === id))).filter(Boolean)
      this.ctx.show = false
    },
    pasteClipboard() {
      if (!this.clipboard.length) return
      const ids = this.clipboard.map((src) => {
        const copy = clone(src)
        copy.id = uid()
        copy.x += 32
        copy.y += 32
        this.page.layers.push(copy)
        return copy.id
      })
      this.selectedIds = ids
      this.ctx.show = false
      this.pushHistory()
    },
    deleteSelected() {
      this.page.layers = this.page.layers.filter((l) => this.selectedIds.indexOf(l.id) === -1)
      this.selectedIds = []
      this.ctx.show = false
      this.pushHistory()
    },
    setSelectedAsBg() {
      const l = this.selectedLayer
      if (!l || (l.type !== 'image' && l.type !== 'video')) return
      l.x = 0
      l.y = 0
      l.width = this.page.width
      l.height = this.page.height
      l.rotation = 0
      const arr = this.page.layers.filter((x) => x.id !== l.id)
      arr.unshift(l)
      this.page.layers = arr
      this.ctx.show = false
      this.pushHistory()
    },
    moveLayer(dir) {
      const id = this.selectedIds[this.selectedIds.length - 1]
      const i = this.page.layers.findIndex((l) => l.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= this.page.layers.length) return
      const arr = this.page.layers.slice()
      const item = arr.splice(i, 1)[0]
      arr.splice(j, 0, item)
      this.page.layers = arr
      this.ctx.show = false
      this.pushHistory()
    },
    nudgeAlign(how) {
      const l = this.selectedLayer
      if (!l) return
      if (how === 'left') l.x = 0
      if (how === 'center') l.x = Math.round((this.page.width - l.width) / 2)
      if (how === 'right') l.x = this.page.width - l.width
      if (how === 'top') l.y = 0
      if (how === 'middle') l.y = Math.round((this.page.height - l.height) / 2)
      if (how === 'bottom') l.y = this.page.height - l.height
      this.menu = null
      this.pushHistory()
    },
    toggleBold() {
      const l = this.selectedLayer
      if (!l) return
      l.fontWeight = (l.fontWeight === '700' || l.fontWeight === '800') ? '400' : '800'
      this.pushHistory()
    },
    cycleAlign() {
      const order = ['left', 'center', 'right']
      const i = order.indexOf(this.selectedLayer.align)
      this.selectedLayer.align = order[(i + 1) % 3]
      this.pushHistory()
    },
    resizeTo(w, h) {
      if (!w || !h) return
      const ox = this.page.width
      const oy = this.page.height
      const sx = w / ox
      const sy = h / oy
      this.page.width = w
      this.page.height = h
      this.page.layers.forEach((l) => {
        l.x = Math.round(l.x * sx)
        l.y = Math.round(l.y * sy)
        l.width = Math.round(l.width * sx)
        l.height = Math.round(l.height * sy)
        if (l.fontSize) l.fontSize = Math.round(l.fontSize * Math.min(sx, sy))
      })
      this.pushHistory()
      this.$nextTick(() => this.fitToScreen())
    },
    async onMagicWrite() {
      const prompt = this.aiPrompt || (this.selectedLayer && this.selectedLayer.text) || this.name || 'Instagram headline'
      this.aiLoading = true
      try {
        const text = await this.generateLayerText({
          prompt,
          role_hint: 'instagram headline, punchy, no quotes',
          max_chars: 80,
        })
        if (this.selectedLayer && this.selectedLayer.type === 'text') this.selectedLayer.text = text
        else this.addText({ text, fontSize: 64, fontWeight: '800' })
        this.pushHistory()
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || 'Magic Write failed')
      } finally {
        this.aiLoading = false
      }
    },
    async onMagicFill() {
      const texts = this.page.layers.filter((l) => l.type === 'text')
      if (!texts.length) {
        this.$toast.error('Add text layers first')
        return
      }
      this.aiLoading = true
      try {
        for (let i = 0; i < texts.length; i += 1) {
          const layer = texts[i]
          const text = await this.generateLayerText({
            prompt: this.aiPrompt || layer.text || this.name,
            role_hint: i === 0 ? 'headline' : 'supporting line',
            max_chars: layer.fontSize > 48 ? 48 : 90,
          })
          layer.text = text
        }
        this.pushHistory()
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || 'Magic Fill failed')
      } finally {
        this.aiLoading = false
      }
    },
    buildPayload() {
      return {
        name: this.name,
        width: this.page.width,
        height: this.page.height,
        background_url: this.page.backgroundUrl || '',
        layers: {
          version: 2,
          pages: this.pages,
          ...(this.canvaSourceUrl ? { canva_source_url: this.canvaSourceUrl } : {}),
        },
      }
    },
    openCanvaReport() {
      this.canvaReportForm = {
        url: this.canvaSourceUrl || '',
        message: '',
      }
      this.canvaReportOpen = true
    },
    async submitCanvaReport() {
      const url = (this.canvaReportForm.url || '').trim()
      const message = (this.canvaReportForm.message || '').trim()
      if (!url || !message) return
      this.canvaReportSending = true
      try {
        await this.reportCanvaImport({
          canva_url: url,
          message,
          design_template_id: this.templateId || '',
        })
        this.canvaReportOpen = false
        this.$toast.success(this.$t('designStudio.report_sent'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('designStudio.report_failed'))
      } finally {
        this.canvaReportSending = false
      }
    },
    async loadTemplate(id) {
      try {
        const t = await this.fetchTemplate(id)
        const doc = parseDocument(t.layers, t)
        this.templateId = t.id
        this.name = t.name
        this.canvaSourceUrl = doc.canvaSourceUrl || ''
        this.pages = applyLibraryToPages(
          doc.pages.length ? doc.pages : [blankPage(t.width, t.height)],
          this.fontLibrary,
        ).map((p) => materializeBackground(p))
        if (t.background_url && this.pages[0] && !(this.pages[0].layers || []).some((l) => l.type === 'image' || l.type === 'video')) {
          this.pages[0].layers.unshift(imageLayer(t.background_url, {
            x: 0, y: 0, width: this.pages[0].width, height: this.pages[0].height,
          }))
        }
        this.injectLayerFonts(this.pages)
        this.pageIndex = 0
        this.selectedIds = []
        this.pushHistory(true)
        this.dirty = false
        this.$router.replace(`/ai/studio/${t.id}`).catch(() => {})
        this.$nextTick(() => this.fitToScreen())
      } catch (e) {
        this.$toast.error('Design not found')
        this.$router.replace('/ai/studio')
      }
    },
    injectLayerFonts(pages) {
      (pages || []).forEach((page) => {
        (page.layers || []).forEach((layer) => {
          if (!layer.fontFamily || !layer.fontUrl) return
          registerFontFace({
            family: layer.fontFamily,
            url: layer.fontUrl,
            weight: layer.fontWeight || '400',
            style: layer.fontStyle || 'normal',
          })
          if (this.fonts.indexOf(layer.fontFamily) === -1) {
            this.fonts = [layer.fontFamily].concat(this.fonts)
          }
        })
      })
    },
    async onSave() {
      if (!this.templateId) return
      if (this.saving) {
        this.scheduleSave()
        return
      }
      this.saving = true
      const snap = this.snapshot()
      try {
        const payload = this.buildPayload()
        const t = await this.updateTemplate({ id: this.templateId, payload })
        this.templateId = t.id
        if (this.snapshot() === snap) this.dirty = false
        else this.scheduleSave()
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || 'Save failed')
      } finally {
        this.saving = false
      }
    },
    measureTextBlock(ctx, text, maxWidth, lineHeight, nowrap) {
      const paragraphs = String(text || '').split('\n')
      let total = 0
      paragraphs.forEach((para) => {
        if (nowrap) {
          total += lineHeight
          return
        }
        const words = para.split(/\s+/).filter(Boolean)
        if (!words.length) {
          total += lineHeight
          return
        }
        let line = ''
        words.forEach((word, i) => {
          const test = line ? `${line} ${word}` : word
          if (ctx.measureText(test).width > maxWidth && line) {
            total += lineHeight
            line = word
          } else {
            line = test
          }
          if (i === words.length - 1) total += lineHeight
        })
      })
      return total
    },
    verticalTextOffset(align, boxH, blockH) {
      const mode = String(align || 'center').toLowerCase()
      if (mode === 'bottom' || mode === 'end') return boxH - blockH
      if (mode === 'top' || mode === 'start') return 0
      return blockH >= boxH ? 0 : (boxH - blockH) / 2
    },
    wrapText(ctx, text, x, y, maxWidth, lineHeight, align, nowrap) {
      const paragraphs = String(text || '').split('\n')
      let yy = y
      paragraphs.forEach((para) => {
        if (nowrap) {
          this.fillLine(ctx, para, x, yy, maxWidth, align)
          yy += lineHeight
          return
        }
        const words = para.split(/\s+/)
        let line = ''
        words.forEach((word, i) => {
          const test = line ? `${line} ${word}` : word
          if (ctx.measureText(test).width > maxWidth && line) {
            this.fillLine(ctx, line, x, yy, maxWidth, align)
            line = word
            yy += lineHeight
          } else {
            line = test
          }
          if (i === words.length - 1) {
            this.fillLine(ctx, line, x, yy, maxWidth, align)
            yy += lineHeight
          }
        })
      })
    },
    fillLine(ctx, line, x, y, maxWidth, align) {
      let tx = x
      if (align === 'center') tx = x + maxWidth / 2
      if (align === 'right') tx = x + maxWidth
      ctx.fillText(line, tx, y)
      if (ctx._strokeOn) ctx.strokeText(line, tx, y)
    },
    loadVideoFrame(url) {
      return new Promise((resolve, reject) => {
        const v = document.createElement('video')
        v.crossOrigin = 'anonymous'
        v.muted = true
        v.preload = 'auto'
        v.onloadeddata = () => resolve(v)
        v.onerror = reject
        v.src = url
      })
    },
    async renderPage(page) {
      const canvas = document.createElement('canvas')
      canvas.width = page.width
      canvas.height = page.height
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = page.backgroundColor || '#fff'
      ctx.fillRect(0, 0, page.width, page.height)
      if (page.backgroundUrl) {
        try {
          const img = await this.loadImage(page.backgroundUrl)
          ctx.drawImage(img, 0, 0, page.width, page.height)
        } catch (e) { /* skip tainted */ }
      }
      for (let i = 0; i < page.layers.length; i += 1) {
        const layer = page.layers[i]
        ctx.save()
        ctx.globalAlpha = layer.opacity == null ? 1 : layer.opacity
        const ox = (layer.rotOx || layer.rotOy) ? layer.rotOx : layer.width / 2
        const oy = (layer.rotOx || layer.rotOy) ? layer.rotOy : (layer.height || 0) / 2
        const cx = layer.x + ox
        const cy = layer.y + oy
        ctx.translate(cx, cy)
        ctx.rotate(((layer.rotation || 0) * Math.PI) / 180)
        ctx.translate(-ox, -oy)
        if (layer.type === 'image' && layer.src) {
          try {
            const img = await this.loadImage(layer.src)
            ctx.filter = layerCssFilter(layer)
            if (layer.path) {
              const vbW = layer.viewBoxW || 64
              const vbH = layer.viewBoxH || 64
              ctx.save()
              ctx.scale(layer.width / vbW, (layer.height || layer.width) / vbH)
              ctx.clip(new Path2D(layer.path))
              if (layer.imageW > 0 && layer.imageH > 0 && layer.objectFit !== 'cover') {
                ctx.drawImage(img, layer.imageX || 0, layer.imageY || 0, layer.imageW, layer.imageH)
              } else {
                const boxW = layer.imageW > 0 ? layer.imageW : vbW
                const boxH = layer.imageH > 0 ? layer.imageH : vbH
                const ox = layer.imageW > 0 ? (layer.imageX || 0) : 0
                const oy = layer.imageH > 0 ? (layer.imageY || 0) : 0
                const s = Math.max(boxW / img.width, boxH / img.height)
                const dw = img.width * s
                const dh = img.height * s
                ctx.drawImage(img, ox + (boxW - dw) / 2, oy + (boxH - dh) / 2, dw, dh)
              }
              ctx.restore()
            } else if (layer.objectFit === 'cover') {
              const z = layer.scale > 1.02 ? layer.scale : 1
              const s = Math.max(layer.width / img.width, (layer.height || layer.width) / img.height) * z
              const dw = img.width * s
              const dh = img.height * s
              ctx.save()
              ctx.beginPath()
              ctx.rect(0, 0, layer.width, layer.height || layer.width)
              ctx.clip()
              ctx.drawImage(img, (layer.width - dw) / 2, ((layer.height || layer.width) - dh) / 2, dw, dh)
              ctx.restore()
            } else {
              ctx.drawImage(img, 0, 0, layer.width, layer.height)
            }
            ctx.filter = 'none'
          } catch (e) { /* skip */ }
        } else if (layer.type === 'video' && layer.src) {
          try {
            const vid = this.videoEl(layer) || await this.loadVideoFrame(layer.src)
            ctx.filter = layerCssFilter(layer)
            ctx.drawImage(vid, 0, 0, layer.width, layer.height)
            ctx.filter = 'none'
          } catch (e) { /* skip */ }
        } else if (layer.type === 'shape') {
          ctx.fillStyle = layer.fill === 'transparent' ? 'rgba(0,0,0,0)' : (layer.fill || '#8b3dff')
          ctx.strokeStyle = layer.stroke || '#000'
          ctx.lineWidth = layer.strokeWidth || 0
          this.drawShapePath(ctx, layer)
          ctx.fill()
          if (layer.strokeWidth) ctx.stroke()
        } else if (layer.type === 'path' && layer.path) {
          const vbW = layer.viewBoxW || 64
          const vbH = layer.viewBoxH || 64
          ctx.scale(layer.width / vbW, (layer.height || layer.width) / vbH)
          ctx.fillStyle = layer.fill || '#000'
          const path2d = new Path2D(layer.path)
          ctx.fill(path2d)
        } else if (layer.type === 'icon') {
          const size = Math.min(layer.width, layer.height || layer.width) * 0.82
          const glyph = iconUnicode(layer.icon)
          if (glyph) {
            ctx.font = `900 ${size}px "Font Awesome 6 Free"`
            ctx.fillStyle = layer.color || '#0d0c22'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(glyph, layer.width / 2, (layer.height || layer.width) / 2)
          }
        } else {
          const size = layer.fontSize || 48
          const weight = layer.fontWeight || 700
          const italic = layer.fontStyle === 'italic' ? 'italic ' : ''
          ctx.font = `${italic}${weight} ${size}px "${layer.fontFamily || 'Montserrat'}", sans-serif`
          ctx.fillStyle = layer.color || '#000'
          ctx.textBaseline = 'top'
          ctx.textAlign = layer.align === 'center' ? 'center' : (layer.align === 'right' ? 'right' : 'left')
          if (layer.uppercase) {
            layer._draw = String(layer.text || '').toUpperCase()
          }
          if (layer.shadow) {
            ctx.shadowColor = 'rgba(0,0,0,0.35)'
            ctx.shadowBlur = 12
            ctx.shadowOffsetY = 6
          }
          ctx._strokeOn = !!layer.outline
          if (layer.outline) {
            ctx.strokeStyle = layer.outlineColor || '#000'
            ctx.lineWidth = layer.outlineWidth || 4
            ctx.lineJoin = 'round'
          }
          const text = layer.uppercase ? String(layer.text || '').toUpperCase() : (layer.text || '')
          const lineHeight = size * (layer.lineHeight || 1.15)
          const blockH = this.measureTextBlock(ctx, text, layer.width, lineHeight, layer.nowrap)
          const startY = this.verticalTextOffset(layer.verticalAlign, layer.height || blockH, blockH)
          this.wrapText(ctx, text, 0, startY, layer.width, lineHeight, layer.align || 'left', layer.nowrap)
          ctx.shadowColor = 'transparent'
        }
        ctx.restore()
      }
      return canvas
    },
    drawShapePath(ctx, layer) {
      const w = layer.width
      const h = layer.height
      ctx.beginPath()
      if (layer.shape === 'ellipse') {
        ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      } else if (layer.shape === 'triangle') {
        ctx.moveTo(w / 2, 0)
        ctx.lineTo(w, h)
        ctx.lineTo(0, h)
        ctx.closePath()
      } else if (layer.shape === 'star') {
        const spikes = 5
        const outer = Math.min(w, h) / 2
        const inner = outer / 2.2
        const cx = w / 2
        const cy = h / 2
        for (let i = 0; i < spikes * 2; i += 1) {
          const r = i % 2 === 0 ? outer : inner
          const a = (i * Math.PI) / spikes - Math.PI / 2
          const x = cx + Math.cos(a) * r
          const y = cy + Math.sin(a) * r
          if (i === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.closePath()
      } else if (layer.shape === 'line') {
        ctx.rect(0, h / 2 - Math.max(4, h / 4), w, Math.max(4, h / 2))
      } else {
        const r = Math.min(layer.radius || 0, w / 2, h / 2)
        if (!r) ctx.rect(0, 0, w, h)
        else {
          ctx.moveTo(r, 0)
          ctx.arcTo(w, 0, w, h, r)
          ctx.arcTo(w, h, 0, h, r)
          ctx.arcTo(0, h, 0, 0, r)
          ctx.arcTo(0, 0, w, 0, r)
        }
      }
    },
    async onDownload() {
      this.exporting = true
      try {
        const canvas = await this.renderPage(this.page)
        const mime = this.exportType === 'jpeg' ? 'image/jpeg' : 'image/png'
        const url = canvas.toDataURL(mime, 0.92)
        const a = document.createElement('a')
        a.href = url
        a.download = `${this.name || 'design'}.${this.exportType === 'jpeg' ? 'jpg' : 'png'}`
        a.click()
        this.menu = null
      } catch (e) {
        this.$toast.error('Download failed')
      } finally {
        this.exporting = false
      }
    },
    async onShareApprove() {
      if (!this.accountId) {
        this.$toast.error('Select an account')
        return
      }
      this.exporting = true
      try {
        await this.onSave()
        const canvas = await this.renderPage(this.page)
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
        const file = new File([blob], `design-${Date.now()}.png`, { type: 'image/png' })
        const uploaded = await this.uploadMedia(file)
        const texts = this.page.layers.filter((l) => l.type === 'text').map((l) => l.text).filter(Boolean)
        await this.studioCreatePost({
          social_account_id: Number(this.accountId),
          type: this.postType,
          caption: this.caption || texts.join('\n'),
          hashtags: [],
          media_urls: [uploaded.url],
          ai_prompt: this.name,
        })
        this.$toast.success('Sent to approval')
        this.$router.push('/posts/approval')
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || 'Share failed')
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.cv {
  --cv-purple: #8b3dff;
  --cv-purple-2: #7d2ae8;
  --cv-ink: #0d0c22;
  --cv-muted: #6d6c80;
  --cv-line: #e6e4ed;
  --cv-stage: #c8ccd0;
  position: relative;
  height: calc(100vh - 64px);
  min-height: 560px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  color: var(--cv-ink);
  font-family: 'Noto Sans', 'Montserrat', sans-serif;
  user-select: none;
}
.cv-top {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px 0 8px;
  border-bottom: 1px solid var(--cv-line);
  background: #fff;
  z-index: 5;
}
.cv-top-left, .cv-top-right, .cv-top-mid {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cv-mark {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--cv-purple), var(--cv-purple-2));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.cv-mark.sm { width: 28px; height: 28px; font-size: 13px; }
.cv-icon-btn, .cv-text-btn, .cv-tbtn, .cv-chip {
  border: 0;
  background: transparent;
  color: var(--cv-ink);
  border-radius: 8px;
  cursor: pointer;
  height: 36px;
  padding: 0 10px;
}
.cv-icon-btn { width: 36px; padding: 0; }
.cv-icon-btn:hover, .cv-text-btn:hover, .cv-tbtn:hover, .cv-chip:hover { background: #f2f1f8; }
.cv-icon-btn:disabled { opacity: 0.35; cursor: default; }
.cv-title {
  height: 32px;
  border: 0;
  background: #f2f1f8;
  border-radius: 8px;
  padding: 0 12px;
  min-width: 160px;
  max-width: 240px;
  font-weight: 600;
  font-size: 13px;
}
.cv-size {
  height: 32px;
  border: 0;
  background: #f2f1f8;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--cv-ink);
  max-width: 260px;
  cursor: pointer;
}
.cv-report-btn { margin-right: 0.35rem; }
.cv-autosave {
  font-size: 12px;
  color: var(--cv-muted);
  min-width: 64px;
  text-align: right;
}
.cv-cloud { font-size: 12px; color: var(--cv-muted); margin-left: 4px; }
.cv-cloud.muted { opacity: 0.8; }
.cv-share {
  background: var(--cv-purple);
  color: #fff;
  border: 0;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}
.cv-share:hover { background: var(--cv-purple-2); }
.cv-share.block { width: 100%; margin-top: 12px; }
.cv-share:disabled { opacity: 0.6; }
.cv-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #0d0c22;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
}
.cv-file-wrap { position: relative; }
.cv-menu {
  position: absolute;
  top: 40px;
  left: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(13,12,34,0.18);
  min-width: 200px;
  padding: 8px;
  z-index: 20;
}
.cv-menu.right { left: auto; right: 0; }
.cv-menu button, .cv-ctx button, .cv-pop button {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: 0;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.cv-menu button:hover, .cv-ctx button:hover, .cv-pop button:hover { background: #f2f1f8; }
.cv-menu hr, .cv-pop hr { margin: 6px 0; border: 0; border-top: 1px solid var(--cv-line); }
.danger { color: #c1121f !important; }
.cv-home {
  position: absolute;
  top: 56px;
  left: 8px;
  width: 260px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(13,12,34,0.16);
  padding: 12px;
  z-index: 30;
}
.cv-home-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.cv-home-head strong { flex: 1; }
.cv-home-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 10px;
  color: var(--cv-ink);
  text-decoration: none;
}
.cv-home-link:hover { background: #f2f1f8; }
.cv-body { display: flex; flex: 1; min-height: 0; }
.cv-rail {
  width: 72px;
  border-right: 1px solid var(--cv-line);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
  gap: 4px;
  background: #fff;
}
.cv-rail-btn {
  width: 64px;
  border: 0;
  background: 0;
  border-radius: 12px;
  padding: 10px 0 8px;
  color: var(--cv-ink);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
}
.cv-rail-btn i { font-size: 18px; }
.cv-rail-btn.on, .cv-rail-btn:hover { background: #f1e9ff; color: var(--cv-purple); }
.cv-panel {
  width: 340px;
  background: #fff;
  border-right: 1px solid var(--cv-line);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.cv-panel-head {
  display: flex;
  align-items: center;
  padding: 14px 12px 8px;
}
.cv-panel-head h3 { flex: 1; font-size: 18px; font-weight: 700; margin: 0; }
.cv-search {
  margin: 0 12px 10px;
  background: #f2f1f8;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  height: 40px;
  color: var(--cv-muted);
}
.cv-search input, .cv-input {
  border: 0;
  background: transparent;
  flex: 1;
  outline: none;
  font-size: 13px;
}
.cv-input {
  width: 100%;
  background: #f2f1f8;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 6px 0 10px;
}
.cv-panel-body { overflow: auto; padding: 0 12px 24px; flex: 1; }
.cv-sec { font-size: 13px; font-weight: 700; margin: 16px 0 8px; }
.cv-empty, .cv-hint { font-size: 12px; color: var(--cv-muted); }
.cv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.cv-card {
  border: 0;
  background: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
}
.cv-card span { display: block; font-size: 12px; margin-top: 6px; font-weight: 600; }
.cv-thumb {
  height: 110px;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eef0f4;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: inset 0 0 0 1px rgba(13,12,34,0.06);
}
.cv-thumb-img,
.cv-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cv-palette-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.cv-palette-card {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.cv-palette-card:hover .cv-palette-preview {
  box-shadow: inset 0 0 0 1px rgba(139, 61, 255, 0.35), 0 6px 16px rgba(13, 12, 34, 0.08);
}
.cv-palette-preview {
  position: relative;
  height: 92px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(13, 12, 34, 0.08);
}
.cv-palette-blob {
  position: absolute;
  border-radius: 50%;
  opacity: 0.88;
}
.cv-palette-blob--a {
  width: 54px;
  height: 54px;
  top: 10px;
  right: 10px;
}
.cv-palette-blob--b {
  width: 30px;
  height: 30px;
  left: 12px;
  bottom: 12px;
  opacity: 0.72;
}
.cv-palette-sample {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Montserrat', sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 8px rgba(13, 12, 34, 0.12);
}
.cv-palette-meta span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin: 6px 0 5px;
}
.cv-palette-dots {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.cv-palette-dots span {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: block;
  box-shadow: inset 0 0 0 1px rgba(13, 12, 34, 0.12);
}
.cv-shapes { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.cv-shape {
  height: 64px;
  border: 0;
  background: #f2f1f8;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cv-shape span { width: 32px; height: 32px; display: block; }
.cv-stickers { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.cv-stickers button {
  height: 56px;
  font-size: 22px;
  color: #0d0c22;
  border: 0;
  background: #f2f1f8;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cv-icn {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-size: inherit;
  line-height: 1;
  pointer-events: auto;
}
.cv-magic, .cv-upload-btn, .cv-tool, .cv-app, .cv-combo, .cv-text-add, .cv-font {
  width: 100%;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  margin-bottom: 8px;
}
.cv-magic, .cv-upload-btn {
  background: #f1e9ff;
  color: var(--cv-purple);
  font-weight: 700;
  padding: 12px;
}
.cv-text-add {
  background: #f2f1f8;
  padding: 12px 14px;
}
.cv-text-add.heading { font-size: 22px; font-weight: 800; }
.cv-text-add.sub { font-size: 16px; font-weight: 600; }
.cv-text-add.body { font-size: 13px; }
.cv-combo, .cv-font, .cv-tool, .cv-app {
  background: #f2f1f8;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cv-app { flex-direction: row; align-items: center; gap: 12px; }
.cv-app i { font-size: 20px; color: var(--cv-purple); width: 28px; }
.cv-app span, .cv-combo span { font-size: 12px; color: var(--cv-muted); }
.cv-colors { display: flex; flex-wrap: wrap; gap: 8px; }
.cv-swatch {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(13,12,34,0.12);
  cursor: pointer;
  padding: 0;
}
.cv-swatch.add {
  background: #f2f1f8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  position: relative;
  overflow: hidden;
}
.cv-swatch.add input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.cv-color-full { width: 100%; height: 36px; border: 0; background: 0; }
.cv-proj { display: flex; gap: 6px; align-items: center; margin-bottom: 8px; }
.cv-proj .grow { flex: 1; }
.cv-work {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--cv-stage);
  position: relative;
}
.cv-float {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(13,12,34,0.16);
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  padding: 5px 6px;
  width: max-content;
  overflow: visible;
}
.cv-float .cv-tbtn {
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.cv-float .cv-tbtn.lbl {
  width: auto;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 600;
}
.cv-float .cv-tbtn.on { background: #f1e9ff; color: var(--cv-purple); }
.cv-sep {
  width: 1px;
  height: 18px;
  background: var(--cv-line);
  margin: 0 4px;
  flex-shrink: 0;
}
.cv-dd { position: relative; flex-shrink: 0; }
.cv-fnt, .cv-sz {
  height: 32px;
  border: 0;
  background: #f2f1f8;
  border-radius: 8px;
  padding: 0 8px;
  flex-shrink: 0;
}
.cv-fnt {
  width: 132px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #0d0c22;
}
.cv-fnt span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cv-fnt i { font-size: 10px; color: var(--cv-muted); }
.cv-fnt.on { background: #f1e9ff; color: var(--cv-purple); }
.cv-fnt-pop { width: 240px; padding: 8px; }
.cv-fnt-search {
  width: 100%;
  height: 32px;
  border: 1px solid var(--cv-line);
  border-radius: 8px;
  padding: 0 10px;
  margin-bottom: 6px;
  background: #f2f1f8;
}
.cv-fnt-list { max-height: 260px; overflow-y: auto; }
.cv-fnt-opt { font-size: 16px; }
.cv-fnt-opt.on, .cv-fnt-opt.hi { background: #f1e9ff; }
.cv-fnt-empty { font-size: 12px; color: var(--cv-muted); padding: 10px; margin: 0; }
.cv-sz { width: 52px; text-align: center; font-size: 13px; }
.cv-col {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid var(--cv-line);
  position: relative;
  flex-shrink: 0;
  margin: 0 4px;
}
.cv-col.ring { box-shadow: inset 0 0 0 4px #fff; }
.cv-col input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.cv-lab { font-size: 11px; color: var(--cv-muted); margin: 0 4px; white-space: nowrap; }
.cv-range { width: 72px; flex-shrink: 0; }
.cv-pop {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 8;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(13,12,34,0.16);
  padding: 10px;
  min-width: 180px;
}
.cv-pop-end { left: auto; right: 0; }
.cv-pop label { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 6px; }
.cv-stage {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 16px;
}
.cv-page {
  position: relative;
  background: #fff;
  box-shadow: 0 12px 48px rgba(13,12,34,0.22);
  flex-shrink: 0;
  overflow: hidden;
}
.cv-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  pointer-events: none;
  z-index: 0;
}
.cv-bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  z-index: 0;
}
.cv-el {
  position: absolute;
  cursor: move;
  box-sizing: border-box;
  overflow: visible;
  pointer-events: none;
}
.cv-el.on,
.cv-el.edit,
.cv-el.lock { pointer-events: auto; }
.cv-el.on { outline: 2px solid var(--cv-purple); }
.cv-el.lock { cursor: default; }
.cv-el.edit { cursor: text; outline-color: #00c4cc; }
.cv-txt {
  width: 100%;
  height: auto;
  white-space: inherit;
  word-break: normal;
  overflow-wrap: normal;
  overflow: visible;
  line-height: inherit;
  outline: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  letter-spacing: inherit;
  pointer-events: none;
}
.cv-el.on .cv-txt,
.cv-el.edit .cv-txt { pointer-events: auto; }
.cv-txt-edit { height: 100%; min-height: 100%; }
.cv-txt-inner { pointer-events: auto; }
.cv-el .h, .cv-el .rot { pointer-events: auto; }
.cv-el img, .cv-el video, .cv-shp, .cv-path { width: 100%; height: 100%; object-fit: fill; pointer-events: auto; display: block; }
.cv-vid { background: #111; }
.cv-seek {
  width: 120px;
  accent-color: var(--cv-purple);
}
.cv-el .h {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #fff;
  border: 2px solid var(--cv-purple);
  border-radius: 2px;
  z-index: 2;
}
.cv-el .h.nw { left: -6px; top: -6px; cursor: nwse-resize; }
.cv-el .h.ne { right: -6px; top: -6px; cursor: nesw-resize; }
.cv-el .h.sw { left: -6px; bottom: -6px; cursor: nesw-resize; }
.cv-el .h.se { right: -6px; bottom: -6px; cursor: nwse-resize; }
.cv-el .h.n { top: -6px; left: 50%; margin-left: -5px; cursor: ns-resize; }
.cv-el .h.s { bottom: -6px; left: 50%; margin-left: -5px; cursor: ns-resize; }
.cv-el .h.w { left: -6px; top: 50%; margin-top: -5px; cursor: ew-resize; }
.cv-el .h.e { right: -6px; top: 50%; margin-top: -5px; cursor: ew-resize; }
.cv-el .rot {
  position: absolute;
  left: 50%;
  top: -32px;
  margin-left: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  color: var(--cv-purple);
  border: 1px solid var(--cv-line);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  font-size: 10px;
}
.cv-pages {
  flex-shrink: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  min-height: 88px;
  background: #e8eaed;
  border-top: 1px solid #d1d5db;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
}
.cv-pages::-webkit-scrollbar { height: 6px; }
.cv-pages::-webkit-scrollbar-thumb {
  background: rgba(63, 63, 70, 0.28);
  border-radius: 99px;
}
.cv-page-thumb {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  scroll-snap-align: center;
}
.cv-page-frame {
  position: relative;
}
.cv-page-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(13, 12, 34, 0.08), 0 0 0 1px rgba(13, 12, 34, 0.12);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.cv-page-preview:hover {
  box-shadow: 0 2px 8px rgba(13, 12, 34, 0.14), 0 0 0 1px rgba(13, 12, 34, 0.16);
}
.cv-page-thumb.on .cv-page-preview {
  box-shadow: 0 0 0 2px var(--cv-purple), 0 2px 10px rgba(124, 58, 237, 0.22);
  transform: translateY(-1px);
}
.cv-page-num {
  font-size: 10px;
  line-height: 1;
  font-weight: 500;
  color: #52525b;
  min-width: 14px;
  text-align: center;
}
.cv-page-thumb.on .cv-page-num {
  color: var(--cv-purple);
  font-weight: 700;
}
.cv-page-del {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border: 0;
  border-radius: 50%;
  background: rgba(24, 24, 27, 0.72);
  color: #fff;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  padding: 0;
  line-height: 1;
  opacity: 0;
  transform: scale(0.92);
  transition: opacity 0.15s ease, transform 0.15s ease, background 0.15s ease;
}
.cv-page-thumb:hover .cv-page-del,
.cv-page-del:focus-visible {
  opacity: 1;
  transform: scale(1);
}
.cv-page-del:hover { background: #ef4444; }
.cv-add-page {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px dashed #9ca3af;
  background: rgba(255, 255, 255, 0.55);
  color: #52525b;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.cv-add-page:hover {
  border-color: var(--cv-purple);
  background: #fff;
  color: var(--cv-purple);
}
.cv-modal {
  position: absolute;
  inset: 0;
  background: rgba(13,12,34,0.28);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 64px 16px 16px;
  z-index: 40;
}
.cv-sheet {
  width: 360px;
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 16px 48px rgba(13,12,34,0.2);
}
.cv-sheet.wide { width: 520px; }
.cv-sheet h4 { margin: 0 0 8px; font-weight: 800; }
.cv-sheet label { font-size: 12px; font-weight: 700; }
.cv-size-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 12px 0;
}
.cv-size-grid button {
  border: 1px solid var(--cv-line);
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  cursor: pointer;
}
.cv-size-grid button:hover { border-color: var(--cv-purple); }
.cv-size-grid span { display: block; font-size: 12px; color: var(--cv-muted); }
.cv-custom { display: flex; gap: 8px; align-items: center; }
.cv-ctx {
  position: fixed;
  z-index: 50;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(13,12,34,0.18);
  padding: 6px;
  min-width: 180px;
}
.d-none { display: none; }
</style>
